import { useLazyQuery } from '@apollo/client';
import type { InvalidItemsError } from '@openx/components-targeting/Targeting/shared/BulkAdd/types.ts';
import { Order_By } from '@openx/types/gqlTypes.ts';
import { enqueueErrorNotification } from '@openx/utils/state/notifications/actions.ts';
import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetSegmentsDocument } from '../../../../../../graphql/query/audiences/GetSegments.generated.tsx';
import type { FormSegment } from '../../../../../../modules/audiences/AudienceForm/types.ts';

type ValidateLocationsParam = {
  search: string[];
  zipSegments: FormSegment[];
  setSearch: Dispatch<SetStateAction<string[]>>;
};

export const useValidateLocations = ({ search = [], zipSegments = [], setSearch }: ValidateLocationsParam) => {
  const { t } = useTranslation();
  const [bulkValidItems, setBulkValidItems] = useState<FormSegment[]>([]);
  const [errors, setErrors] = useState<InvalidItemsError[]>([]);

  const [getSegments, { loading }] = useLazyQuery(GetSegmentsDocument);

  const resetValidationState = useCallback(() => {
    setBulkValidItems([]);
    setErrors([]);
  }, []);

  const handleValidation = useCallback(async () => {
    try {
      resetValidationState();

      const { data } = await getSegments({
        variables: {
          order_by: [{ full_name: Order_By.Asc }],
          where: {
            name: { _in: search },
            category: { _eq: 'location' },
            sub_category: { _eq: 'zip' },
            is_archived: { _eq: false },
            _or: [{ is_nav_only: { _eq: false } }, { is_nav_only: { _is_null: true } }],
          },
        },
      });

      if (data) {
        const validItemsSet = new Set(data.segment.map(({ name }) => name));
        const selectedItemsSet = new Set(zipSegments.map(segment => segment.name));
        const duplicates = new Set<string>();
        const invalidItems = new Set<string>();

        for (const value of search) {
          if (!validItemsSet.has(value)) {
            invalidItems.add(value);
          } else if (selectedItemsSet.has(value)) {
            duplicates.add(value);
          }
        }

        if (invalidItems.size) {
          setErrors(prev => [...prev, { error: t('Invalid Postal Codes.'), invalidItems: Array.from(invalidItems) }]);
          setSearch(prev => prev.filter(item => !invalidItems.has(item)));
        }

        if (duplicates.size) {
          setErrors(prev => [...prev, { error: t('Duplicate Postal Codes.'), invalidItems: Array.from(duplicates) }]);
          setSearch(prev => prev.filter(item => !duplicates.has(item)));
        }

        setBulkValidItems(data.segment as FormSegment[]);
      }
    } catch {
      enqueueErrorNotification(t('Cannot validate zip codes'));
    }
  }, [getSegments, search, resetValidationState, zipSegments, setSearch, t]);

  return {
    bulkValidItems,
    errors,
    handleValidation,
    resetValidationState,
    loading,
  };
};
